import {
  CMP_POLL_INTERVAL_MS,
  CMP_TIMEOUT_MS,
  CONSENT_STATUS,
  DENIED_SNAPSHOT,
} from './constants';
import { readConsentState, subscribeToCmpEvents } from './adapter';

let snapshot = DENIED_SNAPSHOT;
let listeners = [];
let teardown = null;

const isSameConsent = (current, next) => {
  if (current.status !== next.status) {
    return false;
  }

  const currentKeys = Object.keys(current.services);
  const nextKeys = Object.keys(next.services);

  return (
    currentKeys.length === nextKeys.length &&
    nextKeys.every((key) => current.services[key] === next.services[key])
  );
};

const setSnapshot = (next) => {
  // useSyncExternalStore compares snapshots by reference and re-renders on
  // every change. Handing it a fresh object on every read would loop forever,
  // so a new reference is published only when something actually changed.
  if (isSameConsent(snapshot, next)) {
    return;
  }

  snapshot = Object.freeze({
    status: next.status,
    services: Object.freeze({ ...next.services }),
    categories: Object.freeze({ ...next.categories }),
  });

  listeners.forEach((listener) => listener());
};

// Returns true once the CMP has answered, so callers can stop polling.
const syncFromCmp = () => {
  const state = readConsentState();

  if (!state) {
    return false;
  }

  setSnapshot({
    status: CONSENT_STATUS.READY,
    services: state.services,
    categories: state.categories,
  });

  return true;
};

const markUnavailable = () => {
  if (snapshot.status !== CONSENT_STATUS.UNKNOWN) {
    return;
  }

  setSnapshot({
    status: CONSENT_STATUS.UNAVAILABLE,
    services: snapshot.services,
    categories: snapshot.categories,
  });
};

export const initConsentStore = () => {
  if (typeof window === 'undefined' || teardown) {
    return;
  }

  const unsubscribeFromEvents = subscribeToCmpEvents(syncFromCmp);

  // The CMP loader is async and races the Gatsby bundle, so UC_UI_INITIALIZED
  // may already have fired before the listeners above were attached.
  syncFromCmp();

  const startedAt = Date.now();
  const pollId = window.setInterval(() => {
    if (syncFromCmp()) {
      window.clearInterval(pollId);

      return;
    }

    if (Date.now() - startedAt > CMP_TIMEOUT_MS) {
      window.clearInterval(pollId);
      markUnavailable();
    }
  }, CMP_POLL_INTERVAL_MS);

  teardown = () => {
    unsubscribeFromEvents();
    window.clearInterval(pollId);
    teardown = null;
  };
};

export const refreshConsentStore = () => {
  syncFromCmp();
};

export const getConsentSnapshot = () => snapshot;

// SSR and the first client render must agree, otherwise React 18 reports a
// hydration mismatch for every gated embed on the page.
export const getServerConsentSnapshot = () => DENIED_SNAPSHOT;

export const subscribeToConsent = (listener) => {
  listeners = [...listeners, listener];

  return () => {
    listeners = listeners.filter((entry) => entry !== listener);
  };
};

export const resetConsentStore = () => {
  if (teardown) {
    teardown();
  }

  snapshot = DENIED_SNAPSHOT;
  listeners = [];
};
